import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {InputField} from '@Components/input-field-v2';
import type {InputType} from '@Components/input-field-v2/input-field.types';
import {updatePublishingParamTitle} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {useAllPublishingParamsForAccount} from '@Components/social-media/social-media.hooks';
import {getNonStoryPublishingParams} from '@Libraries/social-media-params.library';
import {titleValidations, YOUTUBE_TITLE_ON_SCREEN_MAX} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {useAreCaptionsBeingAutoGenerated, useWizardTitleErrors} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {useAppDispatch} from '@/hooks';
import type {SocialAccount} from '@/components/social-media/account.vo';
import type {SocialMediaPublishingParams} from '@Components/social-media/publish-params.types';
import {noop} from '@Utils/general.util';
import styles from './title-input.module.scss';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import {TITLE_SKELETON_CORNER_RADIUS, TITLE_SKELETON_LINE_HEIGHT_PX} from '@Components/social-media-wizard/components/title-input/title-input.types';

interface TitleInputProps {
  account: SocialAccount;
  setTitleError?: (newValue: string | null) => void;
  areURLsAllowed?: boolean;
}

function getStoredTitle(publishingParams: SocialMediaPublishingParams | null): string {
  if (publishingParams !== null && 'title' in publishingParams) {
    return publishingParams.title;
  }

  return '';
}

export function TitleInput({
  account,
  setTitleError = noop,
  areURLsAllowed = titleValidations[account.type].areURLsAllowed,
}: TitleInputProps): ReactElement | null {
  const allPublishingParams = useAllPublishingParamsForAccount(account.id);
  const storedTitle = getStoredTitle(allPublishingParams ? getNonStoryPublishingParams(allPublishingParams) : null);
  const [title, setTitle] = useState(storedTitle);
  const [titleErrorState, setTitleErrorState] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const titleErrors = useWizardTitleErrors();
  const areCaptionsBeingAutoGenerated = useAreCaptionsBeingAutoGenerated();

  const dispatch = useAppDispatch();

  useEffect((): void => {
    setTitle(storedTitle);
  }, [storedTitle]);

  useEffect((): void => {
    if (titleErrors !== undefined) {
      if (titleErrors[account.id]) {
        handleError();
      } else {
        setTitleError(null);
        setTitleErrorState(false);
      }
    }
  }, [titleErrors]);

  const handleError = (): void => {
    setTitleError(titleErrors[account.id]);
    setTitleErrorState(true);
  };

  const shortenTitleIfNeeded = (inputStr: string): string => {
    if (inputStr.length <= YOUTUBE_TITLE_ON_SCREEN_MAX || account.type !== SocialPlatform.YOUTUBE) {
      return inputStr;
    }

    return `${inputStr.substring(0, YOUTUBE_TITLE_ON_SCREEN_MAX)}...`;
  };

  const onTitleAdded = (inputTitle: InputType): void => {
    const strInput = inputTitle as string;

    setTitle(strInput);
    dispatch(
      updatePublishingParamTitle({
        idSocialMediaAccount: account.id,
        title: strInput,
        areURLsAllowed,
      })
    );
  };

  const getContent = (): ReactElement => {
    if (areCaptionsBeingAutoGenerated) {
      return (
        <div className={`${styles.title} ${styles.titleWritingShell}`}>
          <SkeletonBrick className={styles.titleSkeletonLine} theme={SkeletonBrickTheme.AI_CYCLING_GRADIENT} height={TITLE_SKELETON_LINE_HEIGHT_PX} radius={TITLE_SKELETON_CORNER_RADIUS} />
        </div>
      );
    }

    return (
      <InputField
        input={isUserTyping ? title : shortenTitleIfNeeded(title)}
        onInputBlur={() => {
          setIsUserTyping(false);
        }}
        onInputSubmit={() => {
          setIsUserTyping(false);
        }}
        onInputFocus={() => {
          setIsUserTyping(true);
        }}
        onInputChange={onTitleAdded}
        className={`${styles.title} ${titleErrorState ? styles.titleError : ''}`}
        inputClassName={styles.noBorder}
        placeholder={window.i18next.t('pmwjs_title')}
      />
    );
  };
  return getContent();
}
